<template>
	<div class="selectable-hierarchy-tree">
		<p v-if="flattenedGroups.length === 0" class="tree-empty">Aucun groupe disponible.</p>
		<ul v-else class="tree-list" role="tree">
			<li
				v-for="group in flattenedGroups"
				:key="group.id"
				class="tree-item"
				:class="['tree-item--' + group.type, { 'tree-item--selected': isSelected(group) }]"
				role="treeitem"
			>
				<button v-if="nodeHasChildren(group)" type="button" class="tree-expand" @click.stop="toggleExpand(group)">
					{{ isExpanded(group) ? '▾' : '▸' }}
				</button>
				<span v-else class="tree-expand-spacer" />
				<button type="button" class="tree-label" @click="selectGroup(group)">
					{{ group.name }}
				</button>
			</li>
		</ul>
	</div>
</template>

<script>
export default {
	name: 'SelfSignupHierarchyTree',
	props: {
		hierarchy: {
			type: Array,
			default: function () {
				return [];
			},
		},
		value: {
			type: Object,
			default: null,
		},
	},
	data: function () {
		return {
			expandedIds: {},
		};
	},
	computed: {
		flattenedGroups: function () {
			const result = [];
			const walk = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					const node = nodes[i];
					result.push(node);
					const children = node.children || [];
					if (children.length > 0 && this.isExpanded(node)) {
						walk(children);
					}
				}
			};
			walk(this.hierarchy || []);
			return result;
		},
	},
	watch: {
		hierarchy: {
			immediate: true,
			deep: true,
			handler: function (hierarchy) {
				this.initExpandedIds(hierarchy || []);
			},
		},
	},
	methods: {
		/**
		 * Seeds expand state from hierarchy defaults.
		 * @created_at 29/09/2026 by AI (Composer)
		 */
		initExpandedIds: function (nodes) {
			const next = {};
			const walk = (list) => {
				for (let i = 0; i < list.length; i++) {
					const node = list[i];
					const children = node.children || [];
					if (node.expanded !== false && children.length > 0) {
						next[node.id] = true;
					}
					if (children.length > 0) {
						walk(children);
					}
				}
			};
			walk(nodes);
			this.expandedIds = next;
		},
		nodeHasChildren: function (node) {
			const children = node.children || [];
			return children.length > 0;
		},
		isExpanded: function (node) {
			return this.expandedIds[node.id] === true;
		},
		isSelected: function (node) {
			return this.value != null && this.value.id === node.id;
		},
		toggleExpand: function (node) {
			if (!this.nodeHasChildren(node)) {
				return;
			}
			const next = Object.assign({}, this.expandedIds);
			if (next[node.id]) {
				delete next[node.id];
			} else {
				next[node.id] = true;
			}
			this.expandedIds = next;
		},
		selectGroup: function (node) {
			this.$emit('input', {
				id: node.id,
				name: node.name,
				level: node.level,
				type: node.type,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.selectable-hierarchy-tree {
	border: 1px solid #e0e0e0;
	border-radius: 10px;
	max-height: 260px;
	overflow: auto;
	text-align: left;
	background: white;
	margin-bottom: 16px;
}
.tree-empty {
	padding: 1rem;
	color: #888;
	margin: 0;
}
.tree-list {
	list-style: none;
	margin: 0;
	padding: 0.25rem 0;
}
.tree-item {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding-top: 0.35rem;
	padding-bottom: 0.35rem;
	padding-right: 0.75rem;
	border-bottom: 1px solid #eee;
}
.tree-item--enterprise {
	padding-left: 0.75rem;
	font-weight: 700;
}
.tree-item--subsidiary {
	padding-left: 0.75rem;
}
.tree-item--department {
	padding-left: 2.25rem;
}
.tree-item--team {
	padding-left: 3.75rem;
}
.tree-item--selected {
	background-color: var(--tag-violet-secondary);
	color: var(--tag-violet-primary);
}
.tree-item:not(.tree-item--selected):hover {
	background-color: var(--tag-violet-secondary);
}
.tree-expand,
.tree-label {
	border: none;
	background: transparent;
	cursor: pointer;
	font: inherit;
	color: inherit;
	text-align: left;
}
.tree-expand {
	width: 1.5rem;
	flex-shrink: 0;
	padding: 0;
}
.tree-expand-spacer {
	width: 1.5rem;
	flex-shrink: 0;
	display: inline-block;
}
.tree-label {
	flex: 1;
	padding: 0.15rem 0.25rem;
	border-radius: 6px;
}
</style>
